/**
 * Phase 8 · Cluster C-48 · Step status strip.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md §34.2.C
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §10.2 row 5
 *
 * A horizontal row of pills — one per step — that reflects the
 * current `step_status` for an in-flight plan. Rendered at the top of
 * `AgentPlanCard` but also usable on its own (e.g. pinned to the top
 * of the turn while a long plan executes).
 *
 * Status → colour mapping (intentionally minimalist):
 *   idle    → muted grey (haven't heard from the runner yet)
 *   pending → amber (queued)
 *   running → emerald spinner (in flight)
 *   done    → emerald solid check
 *   failed  → red cross
 *
 * Each pill exposes a tooltip with the tool name + raw status so
 * clicking / hovering gives the operator the full picture when
 * something goes wrong.
 */

import { CheckCircle2, CircleDashed, Loader2, XCircle } from 'lucide-react';
import type { AgentTurnPlan } from '../hooks/useAgentThread';

interface Props {
  plan: AgentTurnPlan;
}

type PillStatus = 'idle' | 'pending' | 'running' | 'done' | 'failed';

export function AgentStepStatus({ plan }: Props) {
  if (!plan.steps.length) return null;
  return (
    <div
      role="list"
      aria-label="Plan step progress"
      className="flex flex-wrap items-center gap-1"
    >
      {plan.steps.map((step, i) => {
        const status: PillStatus =
          plan.stepStatuses[i]?.status ??
          (plan.state === 'pending' || plan.state === 'approved'
            ? 'idle'
            : 'idle');
        const isLast = i === plan.steps.length - 1;
        return (
          <div key={i} role="listitem" className="flex items-center gap-1">
            <Pill index={i + 1} status={status} toolName={step.toolName} />
            {!isLast ? (
              <span
                aria-hidden
                className="inline-block h-[1px] w-2"
                style={{ background: 'var(--vscode-border-primary)' }}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function Pill({
  index,
  status,
  toolName,
}: {
  index: number;
  status: PillStatus;
  toolName: string;
}) {
  const { bg, color, icon } = stylingFor(status);
  return (
    <span
      title={`Step ${index} · ${toolName} · ${status}`}
      className="inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[10px] font-medium"
      style={{ background: bg, color }}
    >
      {icon}
      <span>{index}</span>
    </span>
  );
}

function stylingFor(status: PillStatus): {
  bg: string;
  color: string;
  icon: React.ReactNode;
} {
  switch (status) {
    case 'running':
      return {
        bg: 'color-mix(in srgb, var(--ggj-accent,#10B981) 20%, transparent)',
        color: 'var(--ggj-accent,#10B981)',
        icon: <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden />,
      };
    case 'done':
      return {
        bg: '#D1FAE5',
        color: '#065F46',
        icon: <CheckCircle2 className="h-2.5 w-2.5" aria-hidden />,
      };
    case 'failed':
      return {
        bg: '#FEE2E2',
        color: '#991B1B',
        icon: <XCircle className="h-2.5 w-2.5" aria-hidden />,
      };
    case 'pending':
      return {
        bg: '#FEF3C7',
        color: '#92400E',
        icon: <CircleDashed className="h-2.5 w-2.5" aria-hidden />,
      };
    case 'idle':
    default:
      return {
        bg: 'var(--vscode-bg-hover)',
        color: 'var(--vscode-text-tertiary)',
        icon: <CircleDashed className="h-2.5 w-2.5" aria-hidden />,
      };
  }
}
